import { type ReactElement, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import {
  useLinkProductComponent,
  useListCatalogComponents,
  useListProductComponents,
} from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { V2_COMPONENTS_PATH } from '../utils/routes'

type V2LinkProductComponentFormValues = {
  componentId: string
}

type V2LinkProductComponentDialogProps = {
  productId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onLinked?: () => void
}

export function V2LinkProductComponentDialog({
  productId,
  open,
  onOpenChange,
  onLinked,
}: V2LinkProductComponentDialogProps): ReactElement {
  const { t } = useTranslation('common')
  const { mutateAsync: linkComponent, isPending } =
    useLinkProductComponent(productId)
  const { data: catalogComponents = [], isLoading: isLoadingCatalog } =
    useListCatalogComponents()
  const { data: linkedComponents = [], isLoading: isLoadingLinked } =
    useListProductComponents(productId)

  const form = useForm<V2LinkProductComponentFormValues>({
    defaultValues: {
      componentId: '',
    },
  })

  const linkedComponentIds = useMemo(() => {
    return new Set(linkedComponents.map((row) => row.componentId))
  }, [linkedComponents])

  const availableComponents = useMemo(() => {
    return catalogComponents.filter(
      (component) => !linkedComponentIds.has(component.id)
    )
  }, [catalogComponents, linkedComponentIds])

  const componentSelectItems = useMemo(() => {
    return availableComponents.map((component) => {
      const nameLabel =
        component.name != null && component.name.trim() !== ''
          ? component.name.trim()
          : t('v2.pages.components.fallbackName')
      const label = `${component.sku} — ${nameLabel} (${component.supplier.name})`
      return { value: component.id, label }
    })
  }, [availableComponents, t])

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      form.reset()
    }
    onOpenChange(nextOpen)
  }

  function validateComponentId(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t('v2.pages.products.detail.parts.link.validation.componentRequired')
    }
    return true
  }

  async function handleSubmit(values: V2LinkProductComponentFormValues) {
    try {
      await linkComponent({ componentId: values.componentId })
      form.reset()
      onOpenChange(false)
      onLinked?.()
    } catch {
      toast.error(
        t('v2.pages.products.detail.parts.link.errors.submitFailed')
      )
    }
  }

  const isLoading = isLoadingCatalog || isLoadingLinked
  const showNoComponents = !isLoading && availableComponents.length === 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('v2.pages.products.detail.parts.link.title')}
          </DialogTitle>
          <DialogDescription>
            {t('v2.pages.products.detail.parts.link.description')}
          </DialogDescription>
        </DialogHeader>
        {showNoComponents ? (
          <>
            <Alert variant="info">
              <AlertDescription className="col-start-2 block leading-relaxed">
                <p>
                  {t('v2.pages.products.detail.parts.link.noComponentsLead')}{' '}
                  <Link
                    to={V2_COMPONENTS_PATH}
                    className="font-medium text-sky-800 underline underline-offset-4 hover:text-sky-950 dark:text-sky-200 dark:hover:text-sky-50"
                    onClick={() => {
                      handleOpenChange(false)
                    }}
                  >
                    {t('v2.nav.components')}
                  </Link>{' '}
                  {t('v2.pages.products.detail.parts.link.noComponentsTrail')}
                </p>
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleOpenChange(false)
                }}
              >
                {t('actions.close')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Form {...form}>
            <form
              className="space-y-4"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name="componentId"
                rules={{ validate: validateComponentId }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('v2.pages.products.detail.parts.link.componentLabel')}
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isPending || isLoading}
                      items={componentSelectItems}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t(
                              'v2.pages.products.detail.parts.link.componentPlaceholder'
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableComponents.map((component) => {
                          const nameLabel =
                            component.name != null &&
                            component.name.trim() !== ''
                              ? component.name.trim()
                              : t('v2.pages.components.fallbackName')
                          const label = `${component.sku} — ${nameLabel} (${component.supplier.name})`
                          return (
                            <SelectItem key={component.id} value={component.id}>
                              {label}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => handleOpenChange(false)}
                >
                  {t('actions.cancel')}
                </Button>
                <Button type="submit" disabled={isPending || showNoComponents}>
                  {isPending && (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  )}
                  {t('v2.pages.products.detail.parts.link.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
